'use client';

import { useState, useRef } from 'react';
import { ArrowRight, Check, Play } from 'lucide-react';
import { resolveMediaUrl } from '@/lib/api';
import { parseVideoSource, autoThumbnail, embedUrl } from '@/lib/videoSource';
import { computeImageStyle } from '@/lib/imageStyle';
import { applyTextStyle } from '@/lib/textStyle';

/**
 * ImageTextBlock — bloc image + texte avec PLUSIEURS TEMPLATES de mise en page.
 *
 * Templates disponibles via config.template :
 *   - 'side-by-side'         (défaut) : image et texte côte à côte 50/50
 *   - 'image-dominant'       : image grande 65% / texte 35%
 *   - 'text-dominant'        : texte 60% / image 40% (image discrète)
 *   - 'stack-image-top'      : image PLEINE LARGEUR en haut + texte centré dessous
 *   - 'stack-text-top'       : texte centré en haut + image PLEINE LARGEUR en bas
 *   - 'showcase-fullwidth'   : image XXL pleine largeur sans bordure + texte par-dessus en bas
 *   - 'image-floating-card'  : image très large + card titre/texte qui flotte par-dessus à côté
 *
 * Position image gauche/droite via config.image_position (sauf templates stack/showcase).
 *
 * MEDIA VIDEO (17/08/2026) — le bloc accepte une VIDEO a la place de l'image :
 *   config.media_type === 'video' + config.video_url.
 * Memes plateformes et memes regles que le widget « Video » (YouTube, Vimeo,
 * Dailymotion, fichier .mp4/.webm), en reutilisant SES fonctions : aucune
 * seconde implementation a maintenir. La lecture se fait SUR PLACE, dans le
 * bloc, sans quitter la page ni ouvrir de fenetre.
 *
 * Le lecteur n'est charge QU'AU CLIC : avant lecture, on affiche la vignette
 * et un bouton. Rien n'est demande a YouTube tant que le visiteur n'a rien
 * demande, donc aucun cookie tiers depose sur une simple visite de la page.
 * C'est la meme regle que le widget « Video », et elle a une consequence
 * juridique directe : sans elle, la page deposerait des traceurs sans
 * consentement.
 */
export default function ImageTextBlock({ config }) {
  const videoUrl = config.media_type === 'video' ? (config.video_url || '').trim() : '';
  const imageUrl = resolveMediaUrl(config.image_url);
  // Toutes les mises en page existantes se decidaient sur la seule presence
  // d'une image (sans image, le texte passe pleine largeur). Une video est un
  // media au meme titre : on remplace la condition, jamais la mise en page.
  const hasMedia = !!(videoUrl || imageUrl);
  // Largeur retenue pour les modeles « showcase » et « carte flottante », qui
  // affichaient le media en pleine largeur quoi qu'il arrive.
  const tailleChoisie = config.image_style && config.image_style.size;
  const cadreMedia = tailleChoisie && tailleChoisie !== 'fullwidth'
    ? computeImageStyle(config.image_style).wrapperStyle
    : null;
  const isImageRight = config.image_position === 'right';
  const template = config.template || 'side-by-side';

  // ─── TEMPLATES STACK : image full-width, texte au-dessus/dessous ─────
  if (template === 'stack-image-top' && hasMedia) {
    return (
      <section style={{ padding: '44px 0', background: '#fff' }}>
        <div className="container-page">
          <FullWidthMedia url={imageUrl} videoUrl={videoUrl} config={config} imageStyle={config.image_style} />
          <div style={{ maxWidth: 760, margin: '50px auto 0', textAlign: 'center' }}>
            <TextCol config={config} centered />
          </div>
        </div>
      </section>
    );
  }
  if (template === 'stack-text-top' && hasMedia) {
    return (
      <section style={{ padding: '44px 0', background: '#fff' }}>
        <div className="container-page">
          <div style={{ maxWidth: 760, margin: '0 auto 50px', textAlign: 'center' }}>
            <TextCol config={config} centered />
          </div>
          <FullWidthMedia url={imageUrl} videoUrl={videoUrl} config={config} imageStyle={config.image_style} />
        </div>
      </section>
    );
  }

  // ─── TEMPLATE SHOWCASE : image XXL avec ombre forte, texte en bas en card ─
  if (template === 'showcase-fullwidth' && hasMedia) {
    return (
      <section style={{ padding: '44px 0', background: 'linear-gradient(180deg, #fafafa 0%, #fff 100%)' }}>
        <div className="container-page">
          <div style={{ maxWidth: 720, margin: '0 auto 40px', textAlign: 'center' }}>
            <TextCol config={config} centered hideCta />
          </div>
          <div style={{
            position: 'relative', borderRadius: 0,
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.18)',
            overflow: 'hidden',
            border: '1px solid #e5e7eb',
            ...cadreMedia,
          }}>
            <Media url={imageUrl} videoUrl={videoUrl} config={config} />
          </div>
          {config.cta?.text && (
            <div style={{ textAlign: 'center', marginTop: 32 }}>
              <a href={config.cta.url || '#'} className="btn-primary"
                style={applyTextStyle(config.cta.text_style, { fontSize: 16 })}>
                {config.cta.text} <ArrowRight size={16} />
              </a>
            </div>
          )}
        </div>
      </section>
    );
  }

  // ─── TEMPLATE FLOATING CARD : image XXL + card flottante par-dessus ────
  if (template === 'image-floating-card' && hasMedia) {
    return (
      <section style={{ padding: '44px 0', background: '#fafafa' }}>
        <div className="container-page" style={{ position: 'relative' }}>
          <div style={{
            position: 'relative',
            borderRadius: 0,
            overflow: 'hidden',
            border: '1px solid #e5e7eb',
            boxShadow: '0 16px 50px rgba(15, 23, 42, 0.15)',
            ...cadreMedia,
          }}>
            <Media url={imageUrl} videoUrl={videoUrl} config={config} />
          </div>
          <div className="floating-card" style={{
            position: 'absolute',
            [isImageRight ? 'left' : 'right']: '5%',
            bottom: '-40px',
            maxWidth: 460,
            background: '#fff',
            padding: '32px 36px',
            borderRadius: 0,
            boxShadow: '0 12px 40px rgba(15, 23, 42, 0.18)',
            border: '1px solid #e5e7eb',
          }}>
            <TextCol config={config} compact />
          </div>
          <div style={{ height: 80 }} />
        </div>
      </section>
    );
  }

  // ─── TEMPLATES SIDE-BY-SIDE (par défaut + variantes ratios) ──────────
  let gridCols;
  if (template === 'image-dominant')      gridCols = isImageRight ? '1fr 1.85fr' : '1.85fr 1fr';
  else if (template === 'text-dominant')  gridCols = isImageRight ? '1.5fr 1fr'  : '1fr 1.5fr';
  else /* side-by-side default */         gridCols = isImageRight ? '1fr 1.1fr'  : '1.1fr 1fr';

  // V1.25.1024 — Position verticale de l'image (haut / milieu / bas)
  // pour les templates side-by-side. Defaut = center pour retrocompat.
  const verticalAlign = config.image_vertical_align || 'center';
  const alignItemsMap = { top: 'start', center: 'center', bottom: 'end' };
  const gridAlignItems = alignItemsMap[verticalAlign] || 'center';

  // V1.25.1404 — Position de l'image sur MOBILE (haut / bas du texte).
  // Configurable par bloc dans le CMS. Par défaut on conserve l'ordre
  // desktop (image à gauche -> en haut ; image à droite -> en bas) pour
  // que les pages existantes ne changent pas d'aspect.
  const mobileImgPos = config.image_mobile_position
    || (isImageRight ? 'bottom' : 'top');
  const gridMobileClass = mobileImgPos === 'bottom'
    ? 'img-text-grid--mbottom' : 'img-text-grid--mtop';

  return (
    <section style={{ padding: '44px 0', background: '#fff' }}>
      <div className="container-page">
        <div style={{
          display: 'grid',
          gridTemplateColumns: hasMedia ? gridCols : '1fr',
          gap: '60px',
          alignItems: gridAlignItems,
        }} className={`img-text-grid ${gridMobileClass}`}>
          {!isImageRight && hasMedia && <MediaCol url={imageUrl} videoUrl={videoUrl} config={config} imageStyle={config.image_style} />}
          <TextCol config={config} />
          {isImageRight && hasMedia && <MediaCol url={imageUrl} videoUrl={videoUrl} config={config} imageStyle={config.image_style} />}
        </div>
      </div>
    </section>
  );
}

// ─── LECTEUR VIDEO SUR PLACE ─────────────────────────────────────────
// Presentation et reglages identiques au widget « Video » : vignette avec
// bouton de lecture, chargement du lecteur AU CLIC seulement, embed YouTube
// sans cookie, format 16/9 qui adopte le vrai format du fichier une fois
// connu (aucune bande noire, aucune coupure).
function VideoInline({ config }) {
  const [joue, setJoue] = useState(false);
  const [ratio, setRatio] = useState(null);
  const refVideo = useRef(null);
  const source = parseVideoSource(config.video_url);
  const vignette = autoThumbnail(source, {
    thumbnail_url: config.video_thumbnail_url ? resolveMediaUrl(config.video_thumbnail_url) : null,
  });
  const titre = config.video_title || config.title || 'Vidéo';
  const estFichier = source.kind === 'file';

  // PAS DE BANDES NOIRES (constat Gaetan, 17/08/2026).
  //
  // Le cadre partait sur 16/9 avec un fond noir, et un fichier enregistre dans
  // un autre format se retrouvait avec une bande en haut et une en bas. Deux
  // corrections :
  //   - pour un FICHIER, on lit ses dimensions reelles AVANT la lecture
  //     (preload="metadata") et le cadre prend ce format exact ;
  //   - le fond n'est plus noir. Le peu d'espace residuel se fond dans la
  //     page au lieu de dessiner deux barres.
  // Pour YouTube, Vimeo et Dailymotion le 16/9 reste juste, ce sont eux qui
  // gerent leur propre mise a l'echelle.
  const cadre = {
    position: 'relative', width: '100%', overflow: 'hidden',
    background: 'transparent', aspectRatio: ratio || '16 / 9',
  };

  const surMetadonnees = (e) => {
    const l = e.currentTarget.videoWidth, h = e.currentTarget.videoHeight;
    if (l > 0 && h > 0) setRatio(`${l} / ${h}`);
  };

  const boutonLecture = (
    <span style={{
      position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.18)', cursor: 'pointer',
    }}>
      <span style={{
        width: 64, height: 64, borderRadius: '50%', background: 'rgba(255,255,255,0.94)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 8px 28px rgba(0,0,0,0.35)',
      }}>
        <Play size={26} color="#0f172a" fill="#0f172a" style={{ marginLeft: 3 }} />
      </span>
    </span>
  );

  // ── FICHIER : la balise video est montee tout de suite, sans controles ni
  // lecture. Elle ne telecharge que ses metadonnees, ce qui donne le format
  // exact du cadre des l'affichage de la page.
  if (estFichier) {
    return (
      <div style={cadre}>
        <video
          ref={refVideo}
          preload="metadata"
          playsInline
          controls={joue}
          onLoadedMetadata={surMetadonnees}
          poster={vignette || undefined}
          style={{ width: '100%', height: '100%', display: 'block' }}
        >
          {config.video_url_webm && <source src={config.video_url_webm} type="video/webm" />}
          <source src={config.video_url}
            type={/\.webm(\?.*)?$/i.test(config.video_url) ? 'video/webm' : 'video/mp4'} />
          Votre navigateur ne supporte pas la lecture vidéo.
        </video>
        {!joue && (
          <button type="button" aria-label={`Lire la vidéo : ${titre}`}
            onClick={() => { setJoue(true); refVideo.current && refVideo.current.play(); }}
            style={{ position: 'absolute', inset: 0, border: 0, padding: 0, background: 'transparent', cursor: 'pointer' }}>
            {boutonLecture}
          </button>
        )}
      </div>
    );
  }

  // ── PLATEFORMES : rien n'est demande a YouTube tant que le visiteur n'a
  // rien demande, donc aucun cookie tiers sur une simple visite de la page.
  if (!joue) {
    return (
      <button type="button" onClick={() => setJoue(true)} aria-label={`Lire la vidéo : ${titre}`}
        style={{ ...cadre, border: 0, padding: 0, cursor: 'pointer', display: 'block' }}>
        {vignette ? (
          <img src={vignette} alt={titre}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <span style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg,#1e293b,#0f172a)' }} />
        )}
        {boutonLecture}
      </button>
    );
  }

  return (
    <div style={cadre}>
      <iframe
        src={embedUrl(source)}
        title={titre}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
        allowFullScreen
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0, display: 'block' }}
      />
    </div>
  );
}

// Media = video si le bloc en porte une, image sinon.
function Media({ url, videoUrl, config, imgStyle }) {
  if (videoUrl) return <VideoInline config={config} />;
  return <img src={url} alt={config.title || ''}
    style={imgStyle || { width: '100%', display: 'block', height: 'auto' }} />;
}

// ─── Media pleine largeur (templates stack & showcase) ───────────────
function FullWidthMedia({ url, videoUrl, config, imageStyle }) {
  // La taille CHOISIE est respectee. Avant, ces modeles forcaient la pleine
  // largeur et le reglage « Petite (35%) » n'avait aucun effet : on cliquait
  // sans que rien ne bouge. On ne force la pleine largeur que si AUCUNE
  // taille n'a ete choisie, pour ne pas retrecir les pages deja en ligne.
  const taille = (imageStyle && imageStyle.size) || 'fullwidth';
  const adjusted = { ...(imageStyle || {}), size: taille };
  const { wrapperStyle, imgStyle } = computeImageStyle(adjusted);
  // `maxWidth: 'none'` ANNULAIT la largeur calculee juste au-dessus : ce
  // modele restait en pleine largeur quel que soit le reglage. On ne le force
  // donc QUE lorsque la pleine largeur est effectivement demandee. Sinon on
  // garde la largeur calculee et le media se centre dans la page.
  const largeur = taille === 'fullwidth'
    ? { ...wrapperStyle, maxWidth: 'none' }
    : { ...wrapperStyle, marginLeft: 'auto', marginRight: 'auto' };
  return (
    <div style={{ width: '100%', ...largeur }}>
      <Media url={url} videoUrl={videoUrl} config={config} imgStyle={{ ...imgStyle, width: '100%' }} />
    </div>
  );
}

function TextCol({ config, centered = false, compact = false, hideCta = false }) {
  const cta = config.cta || {};
  const bullets = config.bullets || [];
  // V1.25.1020 — Alignement bullets et CTA configurables.
  // Si le template force "centered" (stack-image-top, showcase…), on force
  // center par défaut. Sinon on prend la valeur configurée dans l'admin.
  const bulletsAlign = config.bullets_align || (centered ? 'center' : 'left');
  const ctaAlign = config.cta_align || (centered ? 'center' : 'left');
  const flexAlignMap = { left: 'flex-start', center: 'center', right: 'flex-end' };
  return (
    <div className="img-text-col-text" style={centered ? { textAlign: 'center' } : undefined}>
      {config.badge_text && (
        <span style={applyTextStyle(config.badge_text_style, {
          display: 'inline-block',
          padding: '6px 14px', borderRadius: '999px',
          background: 'rgba(226, 114, 40, 0.1)',
          color: 'var(--color-primary)',
          fontSize: '13px', fontWeight: 600,
          marginBottom: '16px', letterSpacing: '0.04em',
          textTransform: 'uppercase',
        })}>{config.badge_text}</span>
      )}
      {config.title && (
        <h2 style={applyTextStyle(config.title_style, {
          fontSize: compact ? 'clamp(22px, 2.6vw, 28px)' : 'clamp(28px, 4vw, 40px)',
          fontWeight: 800, lineHeight: 1.15,
          letterSpacing: '-0.02em',
          margin: '0 0 20px',
          color: 'var(--color-text)',
        })}>
          {/* DEAD_CLICKS_V1 (11/08/2026, Clarity) — les visiteurs venus de la
              pub cliquent le TITRE de la carte (« Plans conformes RGIE… »)
              et rien ne se passait : le seul lien était le bouton CTA plus
              bas. Quand le bloc a un lien, le titre mène au même endroit.
              Mêmes styles, même couleur : rien ne change visuellement. */}
          {cta.url ? (
            <a href={cta.url} style={{ color: 'inherit', textDecoration: 'none' }}>
              {config.title}
            </a>
          ) : config.title}
        </h2>
      )}
      {config.description && (
        // V1.25.1420 — Fix critique erreur React #329 (hydratation).
        // L'element porteur de `dangerouslySetInnerHTML` NE DOIT PAS avoir de
        // frere direct : en production, l'hydratation concurrente de React 18
        // deraille quand un noeud dangerouslySetInnerHTML est suivi/precede
        // d'autres elements (titre, puces, CTA) → "Unknown root exit status"
        // → page entiere figee jusqu'au 1er clic. On l'isole donc dans un
        // wrapper dedie dont il est l'UNIQUE enfant (meme schema fiable que
        // RichTextBlock). La classe `img-text-description` reste sur le noeud
        // injecte pour ne pas casser le CSS.
        <div style={centered ? { textAlign: 'center' } : undefined}>
          <div
            className="img-text-description"
            dangerouslySetInnerHTML={{ __html: config.description }}
          />
        </div>
      )}

      {bullets.length > 0 && !compact && (
        <ul style={{
          listStyle: 'none', padding: 0, margin: '24px 0 0',
          display: 'flex', flexDirection: 'column', gap: '12px',
          alignItems: flexAlignMap[bulletsAlign] || 'flex-start',
          textAlign: 'left',
        }}>
          {bullets.map((b, i) => (
            <li key={i} style={{
              display: 'flex', gap: '12px', alignItems: 'flex-start',
              fontSize: '15px', lineHeight: 1.6, color: 'var(--color-text)',
            }}>
              <div style={{
                width: '24px', height: '24px',
                borderRadius: '50%',
                background: 'rgba(226, 114, 40, 0.15)',
                color: 'var(--color-primary)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, marginTop: '1px',
              }}>
                <Check size={14} strokeWidth={3} />
              </div>
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}

      {cta.text && !hideCta && (
        <div style={{ textAlign: ctaAlign, marginTop: compact ? 20 : 32 }}>
          <a
            href={cta.url || '#'}
            className="btn-primary"
            style={applyTextStyle(cta.text_style, {
              fontSize: compact ? 14 : 16,
            })}
          >
            {cta.text} <ArrowRight size={16} />
          </a>
        </div>
      )}
    </div>
  );
}

function MediaCol({ url, videoUrl, config, imageStyle }) {
  const { wrapperStyle, imgStyle } = computeImageStyle(imageStyle);
  return (
    <div className="img-text-col-image" style={{ position: 'relative', ...wrapperStyle }}>
      <Media url={url} videoUrl={videoUrl} config={config} imgStyle={imgStyle} />
    </div>
  );
}
